/*
 * Control de sonido del video del slideshow — capa IG.
 *
 * El video del slideshow se imprime SIEMPRE `muted`: es el requisito de
 * autoplay de los navegadores, y sacarselo al markup dejaria el video sin
 * arrancar (quedaria el poster quieto). Desmutear es siempre cosa de este
 * archivo — y, salvo que el merchant prenda "arrancar con sonido", siempre a
 * partir de un gesto del visitante.
 *
 * Maneja los dos transportes que la seccion sabe imprimir: el <video> de
 * Shopify y el iframe de YouTube / Vimeo (postMessage, mismo contrato que
 * `video-banner.js`).
 */
(() => {
  const BUTTON = '.js-slideshow-sound';
  const SLIDE = '.slideshow__content';
  const ACTIVE = '.swiper-slide-active, .is-current';
  const GESTURES = ['pointerdown', 'keydown', 'touchend'];

  // Un <video> con `display: none` sigue sonando, asi que el par
  // desktop/mobile obliga a mirar cual de los dos esta a la vista.
  const isHidden = (el) => getComputedStyle(el).display === 'none';

  const videosOf = (slide) =>
    [...slide.querySelectorAll('.slideshow__video video')].filter((v) => !isHidden(v));

  const postToFrame = (frame, muted) => {
    if (!frame.contentWindow) return;

    if (frame.classList.contains('js-youtube')) {
      frame.contentWindow.postMessage(
        JSON.stringify({
          event: 'command',
          func: muted ? 'mute' : 'unMute',
          args: '',
        }),
        '*'
      );
    } else if (frame.classList.contains('js-vimeo')) {
      frame.contentWindow.postMessage(
        JSON.stringify({ method: 'setVolume', value: muted ? 0 : 1 }),
        '*'
      );
    }

    frame.classList.toggle('video-muted', muted);
  };

  const apply = (button, muted) => {
    const slide = button.closest(SLIDE);
    if (!slide) return;

    slide.querySelectorAll('.slideshow__video video').forEach((video) => {
      video.muted = muted || isHidden(video);
      /*
       * Siempre reanudar, tambien al mutear: cuando el navegador rechaza el
       * sonido PAUSA el video, y volver a mutearlo no lo despierta solo — el
       * hero quedaba congelado como una foto. Mudo, `play()` nunca se rechaza.
       */
      if (video.paused && !isHidden(video)) {
        const played = video.play();
        if (played) played.catch(() => {});
      }
    });

    slide
      .querySelectorAll('.slideshow__video iframe')
      .forEach((frame) => postToFrame(frame, muted));

    button.classList.toggle('active', !muted);
    button.setAttribute('aria-pressed', String(!muted));

    const label = muted
      ? button.dataset.labelUnmute
      : button.dataset.labelMute;
    if (label) button.setAttribute('aria-label', label);
  };

  /*
   * Arrancar con sonido — `sound_on_by_default`.
   *
   * Ningun navegador deja reproducir con sonido sin que el visitante haya
   * interactuado con la pagina, asi que esto es un INTENTO, no una garantia:
   * si lo rechaza, el video se queda mudo y el boton muestra ese estado real,
   * nunca uno mentiroso.
   *
   * La señal del rechazo es la promesa de `play()` sobre el video ya
   * desmuteado (`NotAllowedError`), mas el chequeo de `paused` — Chrome y
   * Safari PAUSAN un video que pierde el permiso de autoplay al desmutearse.
   */
  const retryOff = new WeakMap();

  const disarmRetry = (button) => {
    const off = retryOff.get(button);
    if (off) off();
  };

  const armRetry = (button) => {
    disarmRetry(button);

    const onGesture = (event) => {
      // El click sobre el propio boton ya lo resuelve su handler.
      if (event.target.closest && event.target.closest(BUTTON)) return;
      off();
      if (button.dataset.soundUserSet !== 'true') apply(button, false);
    };
    const off = () => {
      GESTURES.forEach((type) => document.removeEventListener(type, onGesture));
      retryOff.delete(button);
    };

    GESTURES.forEach((type) => document.addEventListener(type, onGesture));
    retryOff.set(button, off);
  };

  const autoOn = (button) => {
    if (button.dataset.soundUserSet === 'true') return;

    const slide = button.closest(SLIDE);
    if (!slide) return;
    const videos = videosOf(slide);

    /*
     * Hay que esperar a que el autoplay mudo haya arrancado: antes de eso
     * `paused` no distingue "el navegador lo bloqueo" de "todavia no empezo",
     * y se tomaria un falso rechazo.
     */
    if (videos.some((video) => video.paused)) {
      videos.forEach((video) =>
        video.addEventListener('playing', () => autoOn(button), { once: true })
      );
      return;
    }

    apply(button, false);

    Promise.all(
      videos.map((video) => {
        const played = video.play();
        return played || Promise.resolve();
      })
    )
      .then(() => {
        if (videos.some((video) => video.paused)) throw new Error('blocked');
      })
      .catch(() => {
        if (button.dataset.soundUserSet === 'true') return;
        apply(button, true);
        if (button.dataset.soundRetry === 'true') armRetry(button);
      });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest(BUTTON);
    if (!button) return;

    event.preventDefault();
    // A partir de acá manda el visitante: ni el arranque automatico ni su
    // reintento vuelven a tocar el estado.
    button.dataset.soundUserSet = 'true';
    disarmRetry(button);
    apply(button, button.classList.contains('active'));
  });

  /*
   * Al pasar de slide, el video que queda atras no se pausa solo, asi que un
   * slide con sonido seguiria sonando fuera de pantalla. Se observa la clase
   * del propio slide (no el subarbol), por lo que el `apply` de adentro no
   * puede realimentar al observer.
   */
  const watch = (root) => {
    (root || document).querySelectorAll(BUTTON).forEach((button) => {
      const slide = button.closest(SLIDE);
      if (!slide || slide.dataset.igSoundWatched) return;
      slide.dataset.igSoundWatched = 'true';

      new MutationObserver(() => {
        if (!button.classList.contains('active')) return;
        if (slide.matches(ACTIVE)) return;
        apply(button, true);
      }).observe(slide, { attributes: true, attributeFilter: ['class'] });

      /*
       * Y lo mismo al salir de pantalla: el video sigue reproduciendo cuando
       * la seccion queda atras, asi que sin esto el visitante seguiria
       * escuchando un video que ya no ve. Al volver queda mudo — reactivar el
       * sonido solo es una decision del visitante, no del tema.
       *
       * El mismo observer dispara el arranque con sonido, una sola vez por
       * carga: tiene sentido recien cuando el bloque se ve.
       */
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              if (!button.classList.contains('active')) return;
              apply(button, true);
              return;
            }

            if (button.dataset.soundDefault !== 'true') return;
            if (button.dataset.soundAutoDone === 'true') return;
            button.dataset.soundAutoDone = 'true';
            autoOn(button);
          });
        },
        { threshold: 0.25 }
      ).observe(slide);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => watch());
  } else {
    watch();
  }

  document.addEventListener('shopify:section:load', (event) => watch(event.target));
})();
